import { useEffect, useState } from 'react';
import { auditsAPI, type ActionPriority, type ActionStatus, type AuditAction, type AuditActionPayload } from '../../lib/api/audits';
import { getErrorMessage } from '../../hooks/useFetchData';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuditOptions } from './useAuditOptions';

interface ActionDialogProps {
  open: boolean;
  onClose: () => void;
  action?: AuditAction | null;
  /** When raised from an audit: fixes the supplier and links the action. */
  audit?: { id: number; supplier: number; supplier_name: string; reference: string } | null;
  onSaved: (action: AuditAction) => void;
}

interface FormState {
  supplier: string;
  title: string;
  description: string;
  owner: string;
  due_date: string;
  priority: ActionPriority;
  status: ActionStatus;
  completion_notes: string;
}

function initialState(action?: AuditAction | null, audit?: ActionDialogProps['audit']): FormState {
  return {
    supplier: action ? String(action.supplier) : audit ? String(audit.supplier) : '',
    title: action?.title ?? '',
    description: action?.description ?? '',
    owner: action?.owner ? String(action.owner) : '',
    due_date: action?.due_date ?? '',
    priority: action?.priority ?? 'medium',
    status: action?.status ?? 'open',
    completion_notes: action?.completion_notes ?? '',
  };
}

/** Raise a new action (inside or outside an audit) or change an existing one. Customer users only. */
export default function ActionDialog({ open, onClose, action, audit, onSaved }: ActionDialogProps) {
  const [form, setForm] = useState<FormState>(() => initialState(action, audit));
  const [saving, setSaving] = useState(false);
  const options = useAuditOptions(open);
  const supplierFixed = Boolean(audit) || Boolean(action?.audit);

  useEffect(() => {
    if (open) setForm(initialState(action, audit));
  }, [open, action, audit]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    if (!form.supplier) { toast.error('Pick the supplier the action is for.'); return; }
    if (!form.title.trim()) { toast.error('Give the action a title.'); return; }
    if (!form.due_date) { toast.error('Pick a due date.'); return; }
    const payload: AuditActionPayload = {
      audit: action ? action.audit : audit ? audit.id : null,
      supplier: Number(form.supplier),
      title: form.title.trim(),
      description: form.description,
      owner: form.owner ? Number(form.owner) : null,
      due_date: form.due_date,
      priority: form.priority,
      status: form.status,
      completion_notes: form.completion_notes,
    };
    setSaving(true);
    try {
      const res = action ? await auditsAPI.updateAction(action.id, payload) : await auditsAPI.createAction(payload);
      toast.success(action ? 'Action updated' : 'Action raised');
      onSaved(res.data);
      onClose();
    } catch (err: unknown) {
      console.error('Action save failed:', err);
      toast.error(getErrorMessage(err, 'Could not save the action'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="action-dialog">
        <DialogHeader>
          <DialogTitle>
            {action ? 'Edit action' : audit ? `Raise an action from ${audit.reference}` : 'Raise an action'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1.5 md:col-span-2">
            <Label>Supplier</Label>
            {supplierFixed ? (
              <p className="text-sm text-foreground" data-testid="action-supplier-fixed">
                {action?.supplier_name || audit?.supplier_name || options.suppliers.find((s) => s.value === form.supplier)?.label || '…'}
              </p>
            ) : (
              <SearchableSelect options={options.suppliers} value={form.supplier} onChange={(v) => set('supplier', v)}
                                placeholder={options.loading ? 'Loading suppliers…' : 'All suppliers — pick one'} />
            )}
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="action-title">Title</Label>
            <Input id="action-title" value={form.title} onChange={(e) => set('title', e.target.value)}
                   placeholder="e.g. Provide updated public liability certificate" data-testid="action-title" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="action-description">Description</Label>
            <Textarea id="action-description" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Owner</Label>
            <SearchableSelect options={options.people} value={form.owner} onChange={(v) => set('owner', v)} placeholder="Unassigned" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="action-due">Due date</Label>
            <Input id="action-due" type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} data-testid="action-due" />
          </div>
          <div className="space-y-1.5">
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => set('priority', v as ActionPriority)}>
              <SelectTrigger data-testid="action-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set('status', v as ActionStatus)}>
              <SelectTrigger data-testid="action-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.status === 'completed' && (
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="action-notes">Completion notes</Label>
              <Textarea id="action-notes" rows={2} value={form.completion_notes} onChange={(e) => set('completion_notes', e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="action-save">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {action ? 'Save changes' : 'Raise action'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
